import { zodResolver } from '@hookform/resolvers/zod';
import { Mutation, Typenames } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import { SocureProps } from 'features/signup/ApplicationStatus/Review/Socure/Socure';
import { socureDesktopSchema } from 'features/signup/ApplicationStatus/Review/Socure/SocureDesktop.schema';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-web';
import IdvSendSMS from 'services/graphql/mutations/signup/IdvSendSMS';
import FDICDisclaimer from 'shared/components/FDICDisclaimer/FDICDisclaimer';
import Button from 'shared/components/vxl/Button/Button';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isMobileOS, isWeb } from 'shared/constants/platform';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { formatPhoneNumber, PHONE_MAX_LENGTH } from 'shared/utils/string/phone';
import { useMutation } from 'urql';

const SocureDesktop = ({
  socureDocRequest,
  isReactivation,
  isReapplication,
}: SocureProps) => {
  const router = useRouter();
  const [snackbar, setSnackbar] = useState<{
    message: string;
  }>({
    message: '',
  });

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'socure',
  });
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/signup/socure/idv-methods',
    customAttributes: {
      ...(isReactivation && { reactivation_flow: 'true' }),
      ...(isReapplication && { reapplication_flow: 'true' }),
    },
  });

  const [
    { data: sendSMSResult, fetching: idvSendSMSLoading, error: sendSMSError },
    idvSendSMS,
  ] = useMutation<Mutation>(IdvSendSMS);

  useEffect(() => {
    if (sendSMSResult) {
      if (
        sendSMSResult.idvSendSMS.__typename === Typenames.IdvSendSMSError &&
        snackbar.message !== sendSMSResult.idvSendSMS.message
      ) {
        setSnackbar({
          message: sendSMSResult.idvSendSMS.message,
        });
      } else if (snackbar.message !== translatedText('desktopVerify.smsSent')) {
        setSnackbar({
          message: translatedText('desktopVerify.smsSent'),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSMSResult]);

  useEffect(() => {
    if (sendSMSError && snackbar.message !== sendSMSError.message) {
      logErrorEvent(sendSMSError.message, SourceName.SOCURE);
      setSnackbar({
        message: sendSMSError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSMSError]);

  useAutoHideSnackbar(Boolean(snackbar.message), () => {
    setSnackbar({ message: '' });
  });

  const handleBackButtonAction = useCallback(() => {
    if (isReactivation) {
      router.replace('/reactivation');
    } else {
      router.replace('/login');
    }
  }, [isReactivation, router]);

  const {
    control,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phone: '',
    },
    resolver: zodResolver(socureDesktopSchema()),
  });

  const onSubmitSendSMS = async () => {
    if (isValid) {
      const formData = getValues();
      await idvSendSMS({
        input: {
          phonenumber: formData.phone,
          requestId: socureDocRequest?.id,
        },
      });
    }
  };

  return (
    <ScrollView
      style={styles.mainContainer}
      contentContainerStyle={styles.scrollContainer}
    >
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerBackVisible: false,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                {...props}
                primaryOnPress={handleBackButtonAction}
                trailingAccessories={
                  <View style={styles.loginButtonContainer}>
                    <BodyBaseBoldText
                      style={styles.loginButton}
                      onPress={() => {
                        void handleLogout();
                      }}
                      numberOfLines={0}
                    >
                      {translatedText('desktopVerify.logout')}
                    </BodyBaseBoldText>
                  </View>
                }
              />
            ),
          }}
        />
        <>
          <TitleXLText numberOfLines={0} style={styles.title}>
            {translatedText('desktopVerify.title')}
          </TitleXLText>
          <BodyBaseText numberOfLines={0} style={styles.subtitle}>
            {translatedText('desktopVerify.subtitle')}
          </BodyBaseText>
          <Controller
            control={control}
            name="phone"
            render={({ field: { onChange, onBlur, value } }) => {
              const handleChange = (text: string) => {
                onChange(formatPhoneNumber(text));
                onBlur();
              };
              return (
                <View style={styles.input}>
                  <VXLTextInput
                    testID="text-input-desktopVerify.phoneNumberLabel"
                    labelText={translatedText('desktopVerify.phoneNumberLabel')}
                    onChangeText={handleChange}
                    value={value}
                    onBlur={onBlur}
                    keyboardType="phone-pad"
                    maxLength={PHONE_MAX_LENGTH}
                    analyticsId="phoneClickedSocure"
                    customAttributes={{
                      ...(isReactivation && { reactivation_flow: 'true' }),
                      ...(isReapplication && { reapplication_flow: 'true' }),
                    }}
                    errorText={errors.phone?.message}
                  />
                </View>
              );
            }}
          />
          <View style={styles.sendSMSButton}>
            <Button
              label={translatedText('desktopVerify.button')}
              onPress={onSubmitSendSMS}
              isDisabled={!isValid}
              isLoading={idvSendSMSLoading}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
              analyticsId="verificationCodeRequestedSocure"
              customAttributes={{
                verification_type: 'phone',
                ...(isReactivation && { reactivation_flow: 'true' }),
                ...(isReapplication && { reapplication_flow: 'true' }),
              }}
            />
          </View>
        </>
        <View style={styles.horizontalOrContainer}>
          <View style={styles.horizontalOrBorder}></View>
          <BodyBaseBoldText numberOfLines={0} style={styles.horizontalOrText}>
            {translatedText('desktopVerify.or')}
          </BodyBaseBoldText>
          <View style={styles.horizontalOrBorder}></View>
        </View>
        <View style={styles.scanCodeContainer}>
          <TitleXLText numberOfLines={0} style={styles.scanTitle}>
            {translatedText('desktopScan.title')}
          </TitleXLText>
          <BodyBaseText numberOfLines={0} style={styles.scanSubtitle}>
            {translatedText('desktopScan.subtitle')}
          </BodyBaseText>
          <Image
            style={styles.qrCodeImage}
            source={{ uri: socureDocRequest?.qrCode || '' }}
          />
        </View>
      </View>
      <FDICDisclaimer />
      {snackbar.message && isWeb && !isMobileOS ? (
        <Snackbar duration={SNACKBAR_DURATION} text={snackbar.message} />
      ) : undefined}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: theme.spacing.m,
  },
  horizontalOrBorder: {
    borderColor: theme.color.border.primary,
    borderWidth: 1,
    flex: 0.45,
    marginTop: 2,
  },
  horizontalOrContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: 2,
    marginBottom: theme.spacing.xl,
    marginTop: theme.spacing.m,
    width: '100%',
  },
  horizontalOrText: {
    flex: 0.1,
    marginTop: -theme.spacing.s,
    textAlign: 'center',
  },
  input: {
    marginTop: theme.spacing.m,
    width: '100%',
  },
  loginButton: {
    color: theme.color.interactive.primary,
  },
  loginButtonContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  mainContainer: {
    flex: 1,
  },
  qrCodeImage: {
    height: 200,
    width: 200,
  },
  scanCodeContainer: {
    flexShrink: 1,
  },
  scanSubtitle: {
    marginBottom: theme.spacing.m,
  },
  scanTitle: {
    marginVertical: theme.spacing.m,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  sendSMSButton: {
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.l,
    width: '100%',
  },
  subtitle: {
    marginBottom: theme.spacing.m,
  },
  title: {
    marginVertical: theme.spacing.m,
  },
});

export default SocureDesktop;
